/**
 * Offline sources for the sheet viewer and the audio player (installed at app start):
 *
 *  - sheet bundles: a stored copy from a pack ('kgr-content-v1') or the recent LRU ('kgr-recent-v1')
 *    streams exactly like a network response — but only while the bookkeeping lists it (items table
 *    / recent list, which the authoritative manifest prunes); an unlisted copy is a stray and is
 *    deleted instead of served. A stored LARGER tier is preferred over a download (packs keep the
 *    device tier, e.g. 'l' on 11" iPads, while portrait asks for 'm'). Otherwise the bundle is
 *    fetched here and teed into the recent LRU (validated against the manifest), with LRU eviction.
 *    Offline, the closest stored smaller tier (never 's') is served instead of failing.
 *  - audio: a fresh blob: URL of the stored file per call (typed from the stored response); the
 *    player revokes it. null → the player streams from the network.
 *  - a 403 from the media route evicts every stored part of that file, a 404 that revision and
 *    older ones (manager.evictRefusedFile), then the manifest is fetched again.
 */
import type { SongFileDTO, SheetTier } from '../../shared/dto/library';
import { mediaUrl, type ManifestPartInfo } from '../../shared/dto/offline';
import { ApiFailure, apiRaw, isNetworkFailure } from '../api/client';
import { setAudioSourceResolver } from '../audio/audioSource';
import { setSheetBundleSource, type BundleRequest } from '../viewer/sources';
import { openContent, openRecent } from './db';
import { busyUrl } from './downloader';
import { evictRecent, touchRecent } from './lru';
import { evictRefusedFile, offlineStateLoaded, withRecent } from './manager';
import { data, isCurrent } from './store';
import { RECENT_LIMITS } from './types';
import { validateMediaResponse } from './validate';

/** Same long timeout as the viewer's own network path (bundles can be tens of MB). */
const BUNDLE_TIMEOUT_MS = 10 * 60_000;

async function matchIn(cache: Cache | null, url: string): Promise<Response | null> {
  if (!cache) return null;
  try {
    return (await cache.match(url)) ?? null;
  } catch {
    return null;
  }
}

function recentListed(url: string): boolean {
  return data.recent.some((e) => e.urls.some((u) => u.url === url));
}

/**
 * How stored copies may be used: 'checked' — served only when listed, strays deleted; 'unchecked' —
 * the stored state could not be read (IndexedDB trouble): served as offline always does, nothing
 * deleted; 'off' — no user bound: caches are not used at all.
 */
type Mode = 'checked' | 'unchecked' | 'off';

async function storeMode(): Promise<Mode> {
  const s = await offlineStateLoaded();
  return s === 'ok' ? 'checked' : s === 'failed' ? 'unchecked' : 'off';
}

/** A pack copy of `url`, served only while the items table lists it. */
async function fromContent(content: Cache | null, url: string, mode: Mode): Promise<Response | null> {
  if (mode === 'off') return null;
  const hit = await matchIn(content, url);
  if (!hit) return null;
  if (mode === 'unchecked' || data.items.has(url)) return hit;
  void hit.body?.cancel().catch(() => undefined);
  if (url !== busyUrl()) void content!.delete(url).catch(() => false); // stray (see manager.sweepCaches)
  return null;
}

/** A recent-LRU copy of `url`, served only while the recent list lists it. */
async function fromRecent(recent: Cache | null, url: string, mode: Mode): Promise<Response | null> {
  if (mode === 'off') return null;
  const hit = await matchIn(recent, url);
  if (!hit) return null;
  if (mode === 'unchecked' || recentListed(url)) return hit;
  void hit.body?.cancel().catch(() => undefined);
  // Decided inside the list's serialised steps: a copy listed meanwhile stays.
  void withRecent(async (list) => {
    if (!list.some((e) => e.urls.some((u) => u.url === url))) await recent!.delete(url).catch(() => false);
    return null;
  });
  return null;
}

/** A stored copy of the same file at one of `tiers` (in order), from a pack or the recent LRU. */
async function storedTier(req: BundleRequest, tiers: readonly SheetTier[], content: Cache | null, recent: Cache | null, mode: Mode): Promise<Response | null> {
  for (const t of tiers) {
    const url = mediaUrl(req.fileId, req.rev, t);
    const hit = (await fromContent(content, url, mode)) ?? (await fromRecent(recent, url, mode));
    if (hit) return hit;
  }
  return null;
}

/** Larger stored tiers worth serving instead of a download (never for 's', the quick first paint). */
function largerTiers(tier: SheetTier): SheetTier[] {
  return tier === 'm' ? ['l'] : [];
}

/** Smaller stored tiers acceptable when the wanted one cannot be fetched (never 's'). */
function smallerTiers(tier: SheetTier): SheetTier[] {
  return tier === 'l' ? ['m'] : [];
}

/* ─── Recent LRU bookkeeping ────────────────────────────────────────────── */

function songKeyForFile(fileId: string): string {
  const m = data.manifest?.files.find((f) => f.id === fileId);
  return m ? m.songId : `file:${fileId}`;
}

function touch(url: string, fileId: string): void {
  const key = songKeyForFile(fileId);
  if (!data.recent.some((e) => e.key === key && e.urls.some((u) => u.url === url))) return;
  void withRecent(async (list) => touchRecent(list, key, Date.now()));
}

/** The manifest still lists exactly this part (same size): only such copies may enter the LRU. */
function listedPart(req: Pick<BundleRequest, 'fileId' | 'rev' | 'tier'>): ManifestPartInfo | null {
  const f = data.manifest?.files.find((x) => x.id === req.fileId && x.rev === req.rev);
  return f?.parts[req.tier] ?? null;
}

/** Stores a validated network copy in the recent cache, then evicts beyond the limits. */
async function putRecent(gen: number, req: BundleRequest, size: number, res: Response): Promise<void> {
  const cache = await openRecent();
  if (!cache) return;
  const url = req.url;
  try {
    await cache.put(url, res);
  } catch {
    return; // aborted (song closed) or storage full: nothing stored
  }
  if (!isCurrent(gen)) {
    await cache.delete(url).catch(() => false);
    return;
  }
  const key = songKeyForFile(req.fileId);
  await withRecent(async (list) => {
    // Re-checked inside the serialised step: a manifest sync may have dropped the file, a pack may
    // store it now (one copy is enough), or a sweep may already have removed this copy.
    if (data.items.has(url) || listedPart(req)?.size !== size || !(await matchIn(cache, url))) {
      await cache.delete(url).catch(() => false);
      return null;
    }
    const touched = touchRecent(list, key, Date.now(), { url, size });
    const { keep, evicted } = evictRecent(touched, RECENT_LIMITS);
    await Promise.all(evicted.flatMap((e) => e.urls.map((u) => cache.delete(u.url).catch(() => false))));
    return keep;
  });
}

/* ─── Sheet bundles ─────────────────────────────────────────────────────── */

async function sheetBundleSource(req: BundleRequest): Promise<Response | null> {
  const mode = await storeMode();
  const gen = data.generation;
  const [content, recent] = await Promise.all([openContent(), openRecent()]);
  const stored = await fromContent(content, req.url, mode);
  if (stored) return stored;
  const fromLru = await fromRecent(recent, req.url, mode);
  if (fromLru) {
    touch(req.url, req.fileId);
    return fromLru;
  }
  // A stored larger tier of the same revision beats downloading this one, online or not.
  const larger = await storedTier(req, largerTiers(req.tier), content, recent, mode);
  if (larger) return larger;
  if (!navigator.onLine) return storedTier(req, smallerTiers(req.tier), content, recent, mode);

  let res: Response;
  try {
    res = await apiRaw(req.url.replace(/^\/api/, ''), { signal: req.signal, timeoutMs: BUNDLE_TIMEOUT_MS });
  } catch (e) {
    if (isNetworkFailure(e)) {
      const alt = await storedTier(req, smallerTiers(req.tier), content, recent, mode);
      if (alt) return alt;
    }
    if (mode !== 'off' && e instanceof ApiFailure && (e.status === 403 || e.status === 404)) void evictRefusedFile(req.fileId, e.status, req.rev);
    throw e;
  }
  // Tee into the recent LRU only an exact, validated copy of a file the manifest lists.
  const part = listedPart(req);
  if (mode === 'checked' && part && recent && res.body && validateMediaResponse(res, part) === null) {
    void putRecent(gen, req, part.size, res.clone());
  }
  return res;
}

/* ─── Audio ─────────────────────────────────────────────────────────────── */

async function audioSource(file: SongFileDTO): Promise<string | null> {
  if (file.kind !== 'audio' || file.rev < 1) return null;
  const url = mediaUrl(file.id, file.rev, 'orig');
  const hit = await fromContent(await openContent(), url, await storeMode());
  if (!hit) return null;
  const type = hit.headers.get('content-type') || file.origMime || '';
  const blob = await hit.blob();
  const typed = blob.type === type || !type ? blob : blob.slice(0, blob.size, type);
  return URL.createObjectURL(typed);
}

/* ─── Install ───────────────────────────────────────────────────────────── */

let installed = false;

/** Installs the viewer + audio hooks (idempotent). Without the Cache API nothing is installed. */
export function installOfflineSources(): void {
  if (installed || typeof caches === 'undefined') return;
  installed = true;
  setSheetBundleSource(sheetBundleSource);
  setAudioSourceResolver(audioSource);
}

export function uninstallOfflineSources(): void {
  if (!installed) return;
  installed = false;
  setSheetBundleSource(null);
  setAudioSourceResolver(null);
}
